import { SlipEntry } from 'types/SlipEntry';
import { StakeLimit } from 'components/StakeLimit/StakeLimit';
import { MatchOfferModal } from 'components/MatchOfferModal/MatchOfferModal';
import { MultiBonusSlipRow } from 'components/MultiBonus/MultiBonus';
import {
  SystemPicker,
  useSlipType,
} from 'components/SystemPicker/SystemPicker';
import styles from './Slip.module.css';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { SlipContext } from 'context/slipContext';
import {
  SlipCreationModalTypes,
  SlipInputValueTypes,
  ToastMessagesIDs,
} from 'types/enums';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { StartedPick } from 'components/StartedPick/StartedPick';
import { UserContext } from 'context/userContext';
import {
  SlipSubmitTimer,
  SLIP_SUBMIT_TIMER_MODAL_PROPS,
} from 'components/SlipSubmitTimer/SlipSubmitTimer';
import {
  approvedStakeMessage,
  slipRiskMessage,
} from 'helpers/slipRiskMessages';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { SlipCreationModalContent } from 'components/modal-content/SlipCreationModalContent/SlipCreationModalContent';
import { Login } from 'components/modal-content/Login/Login';
import { CURRENCY } from 'App';
import {
  DEFAULT_TOTAL_STAKE,
  LIVE_TICKET_WAIT_MS,
  MAX_WIN_FOR_TICKET,
} from 'config/constants';
import {
  handleSlipInputChange,
  maxStakeFor,
  recomputeSlipValues,
  round2Decimals,
  validateInputs,
} from 'helpers/slipHelper';
import { toast } from 'react-toastify';
import { use2DecimalsNumberState } from 'hooks/use2DecimalsNumberState';
import { translateBetMarketName } from 'helpers/betI18n';
import { describePick, marketLineSuffix } from 'helpers/pickLabel';
import { PickSummary } from 'components/PickSummary/PickSummary';
import {
  SlipPayoutNote,
  marketOddsOf,
} from 'components/PickSummary/SlipPayoutNote';
import { InfoModalWrapper } from 'components/modal-content/InfoModalWrapper/InfoModalWrapper';
import type { LiveQuoteLeg } from 'services/api/slipService';
import { declineSlipCheck } from 'services/api/slipService';
import {
  hasEnabledSelection,
  pruneUnavailableLiveLegsFromEntries,
  prunedSlipMessage,
  runLiveQuoteReviewConfirmPhase,
  applyServerOddsToEntries,
  serverOddsRatio,
  runLiveQuoteVerifyPhase,
  type LiveQuoteReviewRow,
} from 'helpers/liveSlipSubmit';

interface Props {
  entries: SlipEntry[];
  /** Inside the desktop slip dock, whose bar has its own "remove all". */
  docked?: boolean;
  /** Told the payout shown at the bottom, for the dock's closed bar. */
  onPayout?: (payout: number) => void;
}

const DEFAULT_STAKE = Number.parseFloat(
  process.env.REACT_APP_DEFAULT_STAKE ?? DEFAULT_TOTAL_STAKE
);

export const Slip = ({ entries, docked, onPayout }: Props) => {
  const { isSystem, chooseCombination, chooseSystem } = useSlipType();
  const [stakePerBet, setStakePerBet] = use2DecimalsNumberState(DEFAULT_STAKE);
  const [totalStake, setTotalStake] = use2DecimalsNumberState(DEFAULT_STAKE);
  const [maxGain, setMaxGain] = use2DecimalsNumberState(0);
  // The ticket waits for approval: the timer says so.
  const [approvalWait, setApprovalWait] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStartedAt, setSubmitStartedAt] = useState<number | null>(null);
  // Set while a slip is being checked and placed: the timer shows the seconds
  // waited (the server checks a live slip for ~7 s, a prematch one at once).
  const [submitCountdownMs, setSubmitCountdownMs] = useState<number | null>(
    null
  );
  const [slipCreated, setSlipCreated] = useState(false);
  const [insufficientFundsError, setInsufficientFundsError] = useState(false);
  const [slipModalView, setSlipModalView] = useState<SlipCreationModalTypes>(
    SlipCreationModalTypes.CONFIRM_CREATING
  );
  const [quoteReviewRows, setQuoteReviewRows] = useState<LiveQuoteReviewRow[]>(
    []
  );
  const [lastVerifyLegs, setLastVerifyLegs] = useState<LiveQuoteLeg[]>([]);
  const {
    betNumber,
    removeSlipEntry,
    maxCoefficient,
    payoutCoefficient,
    removeAllEntries,
    replaceSlipEntries,
    submitSlip,
    enableDisableBetOdd,
    acceptedOddChanges,
    setAcceptedOddChanges,
  } = useContext(SlipContext);
  const { t } = useTranslation();
  const { loggedUser } = useContext(UserContext);

  // The stake per bet stays when the number of bets changes, so the total
  // follows it; the potential win follows both.
  useEffect(() => {
    const total = betNumber > 1 ? stakePerBet * betNumber : totalStake;
    if (betNumber > 1) setTotalStake(total);
    setMaxGain(total * payoutCoefficient);
  }, [betNumber, payoutCoefficient]);

  useEffect(() => {
    onPayout?.(maxGain ? Math.min(maxGain, MAX_WIN_FOR_TICKET) : 0);
  }, [maxGain, onPayout]);

  // The match whose whole offer is open from the slip (prematch); live
  // matches open the live page, which lists their markets.
  const navigate = useNavigate();
  const [offerFi, setOfferFi] = useState<number | string | null>(null);
  const openMatchOffer = (entry: SlipEntry) => {
    if (entry.isLive) {
      navigate('/live-betting');
    } else {
      setOfferFi(entry.match.fi);
    }
  };

  // While a field is typed in, the others follow it at once.
  const [editingGain, setEditingGain] = useState(false);
  const liveRecompute = (version: SlipInputValueTypes, raw: string) =>
    recomputeSlipValues(
      version,
      Number.parseFloat(raw),
      { betNumber, maxCoefficient: payoutCoefficient },
      { setTotalStake, setStakePerBet, setMaxGain }
    );

  const handleInputChange = (event: any, version: SlipInputValueTypes) => {
    const newValue = round2Decimals(event.target.value);
    try {
      handleSlipInputChange(
        version,
        newValue,
        {
          betNumber,
          maxCoefficient: payoutCoefficient,
        },
        { setTotalStake, setStakePerBet, setMaxGain },
        t
      );
      toast.dismiss(ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR);
    } catch (e: any) {
      toast(t(e.message), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      if (betNumber > 1) {
        setStakePerBet(DEFAULT_STAKE);
        setTotalStake(DEFAULT_STAKE * betNumber);
        setMaxGain(DEFAULT_STAKE * maxCoefficient);
      } else {
        setTotalStake(DEFAULT_STAKE);
        setMaxGain(DEFAULT_STAKE * payoutCoefficient);
      }
    }
  };

  const resetSlipSubmitModal = () => {
    setSlipCreated(false);
    setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    setQuoteReviewRows([]);
    setLastVerifyLegs([]);
  };

  const handleSubmitSlipClick = () => {
    // Only started (or suspended) picks left: nothing to place.
    if (!hasEnabledSelection(entries)) {
      toast(t('slip_no_active_picks_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      return;
    }
    try {
      validateInputs(
        totalStake,
        betNumber > 1 ? stakePerBet : undefined,
        SlipInputValueTypes.TOTAL_STAKE,
        t
      );
      resetSlipSubmitModal();
      setModalOpen(true);
    } catch (e: any) {
      toast(e.message, {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
    }
  };

  const preventMinusSign = (event: any) => {
    if (event.code === 'Minus' || event.code === 'KeyE') {
      event.preventDefault();
    }
  };

  const validatePaste = (event: any) => {
    const clipboardData = event.clipboardData;
    const pastedData = Number.parseFloat(clipboardData.getData('text'));
    if (Number.isNaN(pastedData) || pastedData < 0) {
      event.preventDefault();
    }
  };

  // Submits straight away. The wait before a live ticket is the server's quote
  // check (runLiveQuoteVerifyPhase); a prematch slip has nothing to wait for.
  const executeCreateTicket = async () => {
    setIsLoading(true);
    if (insufficientFundsError) {
      setInsufficientFundsError(false);
    }
    const result = await submitSlip(
      totalStake,
      loggedUser,
      (seconds: number) => {
        setApprovalWait(true);
        setSubmitStartedAt(Date.now());
        setSubmitCountdownMs(seconds * 1000);
      }
    );
    setApprovalWait(false);
    setIsLoading(false);
    const riskMessage = slipRiskMessage(result, t);
    if (result.ok && result.approvedStake) {
      toast(approvedStakeMessage(result.approvedStake, t), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
    }
    if (!result.ok && riskMessage) {
      toast(riskMessage, { toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR });
      // The largest stake still taken goes into the stake box, so the
      // player only confirms again.
      if (
        result.code === 'risk_max_stake' &&
        result.maxStake &&
        betNumber <= 1
      ) {
        setTotalStake(result.maxStake);
        setMaxGain(result.maxStake * payoutCoefficient);
      }
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (result.ok) {
      setSlipCreated(true);
      setSlipModalView(SlipCreationModalTypes.SLIP_CREATED);
    } else if (result.insufficientFunds) {
      setInsufficientFundsError(true);
    } else if (result.code === 'limit_reached' && result.limit) {
      toast(
        t('limit_reached_message', {
          limit: t(`limit_kind_${result.limit.kind}`),
          period: t(`limit_period_${result.limit.period}`),
          remaining: result.limit.remaining.toFixed(2),
          currency: process.env.REACT_APP_CURRENCY ?? '',
        }),
        { toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR }
      );
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (result.code === 'account_locked') {
      toast(t('account_locked_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setModalOpen(false);
      resetSlipSubmitModal();
    } else if (/Same ticket already placed/i.test(result.error)) {
      toast(t('duplicate_ticket_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (/could not be confirmed/i.test(result.error)) {
      toast(t('quotes_unconfirmed_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (/odd not found in live feed/i.test(result.error)) {
      toast(t('live_quote_selection_unavailable'), {
        toastId: ToastMessagesIDs.LIVE_SLIP_SELECTION_REMOVED,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else if (/Potential win amount is greater than/i.test(result.error)) {
      toast(t('max_win_exceeded_message'), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
    } else {
      toast(result.error, {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setModalOpen(false);
      resetSlipSubmitModal();
    }
  };

  // The provider's prices pushed a slip that was within the ticket maximum
  // over it: stop and say so, rather than place it with the win capped.
  const stopIfOddsPushedOverLimit = (legs: LiveQuoteLeg[]): boolean => {
    const ratio = serverOddsRatio(legs);
    const before = totalStake * payoutCoefficient;
    if (before > MAX_WIN_FOR_TICKET || before * ratio <= MAX_WIN_FOR_TICKET) {
      return false;
    }
    toast(
      t('odds_changed_over_limit_message', {
        amount: maxStakeFor(payoutCoefficient * ratio).toFixed(2),
        currency: CURRENCY,
      }),
      { toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR }
    );
    setModalOpen(false);
    resetSlipSubmitModal();
    return true;
  };

  // keepTrying: the player said yes to the tries after a suspended price, so
  // a try with no answer simply goes on to the next, each with its own count,
  // until the server says none are left.
  const onConfirmSubmitFirstStep = async (keepTrying = false) => {
    setSubmitStartedAt(Date.now());
    // Every slip is checked with the provider first (prematch too), which can
    // take a few seconds, so the timer shows for all of them.
    setSubmitCountdownMs(LIVE_TICKET_WAIT_MS);
    setIsLoading(true);
    const phase = await runLiveQuoteVerifyPhase(entries, acceptedOddChanges, t);
    if (phase.kind === 'quote_suspended' && keepTrying) {
      await onConfirmSubmitFirstStep(true);
      return;
    }
    setIsLoading(false);
    if (phase.kind === 'error') {
      toast(t(phase.toastKey), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      return;
    }
    if (phase.kind === 'quote_suspended') {
      setSlipModalView(SlipCreationModalTypes.QUOTE_SUSPENDED);
      return;
    }
    if (phase.kind === 'live_unavailable_prune') {
      const { nextEntries, removedTitles, startedTitles } =
        pruneUnavailableLiveLegsFromEntries(entries, phase.legs);
      replaceSlipEntries(nextEntries);
      toast(prunedSlipMessage(t, removedTitles, startedTitles), {
        toastId: ToastMessagesIDs.LIVE_SLIP_SELECTION_REMOVED,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      setQuoteReviewRows([]);
      setLastVerifyLegs([]);
      if (!hasEnabledSelection(nextEntries)) {
        setModalOpen(false);
        resetSlipSubmitModal();
      }
      return;
    }
    if (phase.kind === 'quote_review') {
      setLastVerifyLegs(phase.legs);
      setQuoteReviewRows(phase.rows);
      setSlipModalView(SlipCreationModalTypes.LIVE_QUOTE_REVIEW);
      return;
    }
    const legs = phase.kind === 'no_live' ? [] : phase.legs;
    setLastVerifyLegs(legs);
    // Show the prices the ticket is written at, so the slip's potential win
    // matches the server's.
    const priced = applyServerOddsToEntries(entries, legs);
    if (priced) replaceSlipEntries(priced);
    if (stopIfOddsPushedOverLimit(legs)) return;
    await executeCreateTicket();
  };

  const onConfirmQuoteReviewSubmit = async () => {
    setSubmitStartedAt(Date.now());
    setSubmitCountdownMs(LIVE_TICKET_WAIT_MS);
    setIsLoading(true);
    const phase = await runLiveQuoteReviewConfirmPhase(
      entries,
      acceptedOddChanges,
      lastVerifyLegs,
      t
    );
    setIsLoading(false);
    if (phase.kind === 'error') {
      toast(t(phase.toastKey), {
        toastId: ToastMessagesIDs.SLIP_INPUT_VALUE_ERROR,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      return;
    }
    if (phase.kind === 'quote_suspended') {
      setSlipModalView(SlipCreationModalTypes.QUOTE_SUSPENDED);
      return;
    }
    if (phase.kind === 'live_unavailable_prune') {
      const { nextEntries, removedTitles, startedTitles } =
        pruneUnavailableLiveLegsFromEntries(entries, phase.legs);
      replaceSlipEntries(nextEntries);
      toast(prunedSlipMessage(t, removedTitles, startedTitles), {
        toastId: ToastMessagesIDs.LIVE_SLIP_SELECTION_REMOVED,
      });
      setSlipModalView(SlipCreationModalTypes.CONFIRM_CREATING);
      setQuoteReviewRows([]);
      setLastVerifyLegs([]);
      if (!hasEnabledSelection(nextEntries)) {
        setModalOpen(false);
        resetSlipSubmitModal();
      }
      return;
    }
    if (phase.kind === 'quote_review_still') {
      setLastVerifyLegs(phase.legs);
      setQuoteReviewRows(phase.rows);
      return;
    }
    setLastVerifyLegs(phase.legs);
    const priced = applyServerOddsToEntries(entries, phase.legs);
    if (priced) replaceSlipEntries(priced);
    if (stopIfOddsPushedOverLimit(phase.legs)) return;
    await executeCreateTicket();
  };

  const closeModalOnly = () => {
    setModalOpen(false);
    resetSlipSubmitModal();
  };

  const clearSlip = () => {
    removeAllEntries();
    setModalOpen(false);
    resetSlipSubmitModal();
  };

  const modalConfirmHandler = slipCreated
    ? clearSlip
    : slipModalView === SlipCreationModalTypes.LIVE_QUOTE_REVIEW
      ? onConfirmQuoteReviewSubmit
      : () =>
          onConfirmSubmitFirstStep(
            slipModalView === SlipCreationModalTypes.QUOTE_SUSPENDED
          );

  const modalRejectHandler = () => {
    if (slipCreated) {
      clearSlip();
      return;
    }
    if (slipModalView === SlipCreationModalTypes.QUOTE_SUSPENDED) {
      declineSlipCheck();
    }
    closeModalOnly();
  };

  return (
    <div className={styles.wrapper}>
      <Modal
        isOpen={modalOpen}
        ariaHideApp={false}
        onRequestClose={() => !loggedUser && setModalOpen(false)}
        {...(isLoading ? SLIP_SUBMIT_TIMER_MODAL_PROPS : {})}
      >
        {isLoading && submitCountdownMs != null && (
          <SlipSubmitTimer
            key={submitStartedAt}
            startedAt={submitStartedAt}
            seconds={Math.ceil(submitCountdownMs / 1000)}
            title={approvalWait ? t('approval_pending_title') : undefined}
            hint={approvalWait ? t('approval_pending_hint') : undefined}
          />
        )}
        {loggedUser && insufficientFundsError && (
          <InfoModalWrapper
            onClose={() => {
              setModalOpen(false);
              resetSlipSubmitModal();
            }}
          >
            <InsufficientFundsMessage />
          </InfoModalWrapper>
        )}
        {loggedUser && !insufficientFundsError && !isLoading && (
          <SlipCreationModalContent
            modalType={
              slipCreated ? SlipCreationModalTypes.SLIP_CREATED : slipModalView
            }
            onReject={modalRejectHandler}
            onConfirm={modalConfirmHandler}
            liveQuoteReviewRows={quoteReviewRows}
          />
        )}
        {!loggedUser && <Login setModalOpen={setModalOpen} />}
      </Modal>
      <div className={styles.slipTypeWrapper}>
        <div
          className={classNames(styles.slipType, {
            [styles['slipTypeSelected']]: !isSystem,
          })}
          onClick={chooseCombination}
        >
          {t('combination_label')}
        </div>
        <div
          className={classNames(styles.slipType, {
            [styles['slipTypeSelected']]: isSystem,
          })}
          onClick={chooseSystem}
        >
          {t('system_label')}
        </div>
      </div>
      <SystemPicker />
      {entries.map((entry, matchIndex) => (
        <div className={styles.entry} key={matchIndex}>
          <div className={styles.teams}>
            {/* The match opens its whole offer, to swap a pick in place. */}
            <span
              className={styles.teamsLink}
              role="button"
              tabIndex={0}
              onClick={() => openMatchOffer(entry)}
              onKeyDown={(e) => e.key === 'Enter' && openMatchOffer(entry)}
            >
              {entry.match.home?.name ??
                (typeof entry.match.league === 'string'
                  ? entry.match.league
                  : entry.match.league?.name) ??
                'Unknown'}{' '}
              - {entry.match.away?.name ?? '-'}
            </span>{' '}
            {entry.isLive ? <span className={styles.live}>(LIVE)</span> : ''}
            <ThemeIcon
              icon="remove-black"
              alt="Remove"
              variant="inline"
              onClick={() => removeSlipEntry(entry.match)}
            />
          </div>
          {entry.bets.map((betOdd, betIndex) => {
            const translatedMarket = translateBetMarketName(betOdd.bet, t);
            const pick = describePick(
              {
                betName: betOdd.bet.name,
                odd: betOdd.odd,
                home: entry.match.home?.name,
                away: entry.match.away?.name,
                marketOdds: marketOddsOf(entry, betOdd.bet.name),
              },
              t
            );
            return (
              <div
                className={styles.bet}
                key={betIndex}
                data-is-disabled={betOdd.disabled}
                data-started={!!betOdd.started}
              >
                <PickSummary
                  info={{
                    ...pick,
                    market:
                      translatedMarket +
                      marketLineSuffix(betOdd.bet.name, betOdd.odd),
                  }}
                  odds={betOdd.odd.odds}
                  pickClassName={styles.oddName}
                  match={`${entry.match.home?.name ?? ''} - ${entry.match.away?.name ?? ''}`}
                />
                <span className={styles.betOdds}>
                  <span className={styles.oddValue}>
                    {betOdd.odd.odds.toFixed(2)}
                  </span>
                  <div className={styles.betIcons}>
                    <ThemeIcon
                      icon="check-lg"
                      alt="Disable"
                      variant="inline"
                      accent={!betOdd.disabled}
                      onClick={() =>
                        enableDisableBetOdd(
                          entry.match,
                          betOdd.bet,
                          betOdd.odd,
                          !betOdd.disabled
                        )
                      }
                    />
                    {entry.bets.length > 1 && (
                      <ThemeIcon
                        icon="remove-black"
                        alt="Remove Bet"
                        variant="inline"
                        onClick={() =>
                          removeSlipEntry(entry.match, betOdd.bet, betOdd.odd)
                        }
                      />
                    )}
                  </div>
                </span>
                {betOdd.started && <StartedPick fi={entry.match.fi} />}
              </div>
            );
          })}
        </div>
      ))}
      {!docked && (entries.length > 1 || entries[0].bets.length > 1) && (
        <div className={styles.removeAll} onClick={() => removeAllEntries()}>
          {t('remove_all_bets_label')}
        </div>
      )}
      <StakeLimit
        totalStake={totalStake}
        maxCoefficient={payoutCoefficient}
        onStakeMax={(stake) =>
          handleInputChange(
            { target: { value: stake } },
            SlipInputValueTypes.TOTAL_STAKE
          )
        }
      />
      <div className={styles.slipDetails}>
        <div>
          <span>{t('number_of_events_label')}</span>
          <span>{entries.length}</span>
        </div>
        {betNumber > 1 && (
          <>
            <div>
              <span>{t('number_of_bets_label')}</span>
              <span>{betNumber}</span>
            </div>
            <div>
              <span>{t('stake_per_bet_label')}</span>
              <span>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={stakePerBet ? stakePerBet : ''}
                  onBlur={(event) =>
                    handleInputChange(event, SlipInputValueTypes.STAKE_PER_BET)
                  }
                  onChange={(event) =>
                    liveRecompute(
                      SlipInputValueTypes.STAKE_PER_BET,
                      event.target.value
                    )
                  }
                  onKeyDown={preventMinusSign}
                  onPaste={validatePaste}
                />{' '}
                {CURRENCY}
              </span>
            </div>
          </>
        )}
        <div>
          <span>{t('total_stake_label')}</span>
          <span>
            <input
              type="number"
              step={0.01}
              min="0.01"
              maxLength={16}
              value={totalStake ? totalStake : ''}
              onChange={(event) =>
                liveRecompute(
                  SlipInputValueTypes.TOTAL_STAKE,
                  event.target.value
                )
              }
              onKeyDown={preventMinusSign}
              onPaste={validatePaste}
              onBlur={(event) =>
                handleInputChange(event, SlipInputValueTypes.TOTAL_STAKE)
              }
            />{' '}
            {CURRENCY}
          </span>
        </div>
        <div>
          <span>{t('max_coefficient_label')}</span>
          <span>{maxCoefficient.toFixed(2)}</span>
        </div>
        <div>
          <span>{t('max_gain_label')}</span>
          <span>
            <input
              type="number"
              min="0"
              maxLength={16}
              value={
                maxGain
                  ? editingGain
                    ? maxGain
                    : Math.min(maxGain, MAX_WIN_FOR_TICKET)
                  : ''
              }
              onChange={(event) =>
                liveRecompute(SlipInputValueTypes.MAX_GAIN, event.target.value)
              }
              onKeyDown={preventMinusSign}
              onPaste={validatePaste}
              step={0.01}
              onBlur={(event) => {
                setEditingGain(false);
                handleInputChange(event, SlipInputValueTypes.MAX_GAIN);
              }}
              onFocus={() => setEditingGain(true)}
            />{' '}
            {CURRENCY}
          </span>
        </div>
        <div>
          <span>{t('possible_payout_label')}</span>
          <span>
            {maxGain ? Math.min(maxGain, MAX_WIN_FOR_TICKET).toFixed(2) : 0}{' '}
            {CURRENCY}
          </span>
        </div>
        {/* A system plays no bonus on the number of matches. */}
        {!isSystem && (
          <MultiBonusSlipRow
            entries={entries}
            win={maxGain ? Math.min(maxGain, MAX_WIN_FOR_TICKET) : 0}
            maxWin={MAX_WIN_FOR_TICKET}
          />
        )}
        <SlipPayoutNote
          className={styles.slipNote}
          bets={betNumber}
          system={isSystem}
        />
      </div>
      <div
        className={classNames(styles.acceptOddsChange, {
          [styles['acceptedOddChanges']]: acceptedOddChanges,
        })}
        onClick={() => setAcceptedOddChanges(!acceptedOddChanges)}
      >
        <ThemeIcon
          icon="checkmark"
          alt="Checkmark"
          variant="inline"
          active={acceptedOddChanges}
        />
        {t('accept_odds_change_label')}
      </div>
      {offerFi != null && (
        <MatchOfferModal fi={offerFi} onClose={() => setOfferFi(null)} />
      )}
      <button
        className={styles.submitBetButton}
        onClick={handleSubmitSlipClick}
      >
        <ThemeIcon
          icon="chevron-right-white"
          alt="Submit Bet"
          variant="inline"
          tone="chrome"
        />
        {t('submit_bet_label')}
      </button>
    </div>
  );
};

// Pick labels live in helpers/pickLabel; re-exported for older imports.
export { getOddLabel, marketLineSuffix } from 'helpers/pickLabel';

const InsufficientFundsMessage = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.insufficientFundsWrapper}>
      <ThemeIcon icon="info-full" alt="" variant="inline" />
      <span>{t('insufficient_funds_error_message')}</span>
    </div>
  );
};
